import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ScreenSizeDetectorService, UiKitModule } from '@serviceplan/ui-kit';
import { AssetBasketService, DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  AssetsGenerateUrlService,
  CanPipe,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  FilterAllOptions,
  FilterFormDataModel,
  NotificationService,
  ResizeService,
  ScreenSize,
  SearchAssetsFieldPlaceholder,
  SpDateFormatPipe,
  TagsDataConfig,
  ViewType,
  accessAsset,
  anyAsset,
  anyGlobal,
} from '@thehubz/client/core';
import { Asset, AssetsApiService, AssetsBulkEditApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, MAX_SEARCH_TERMS, SEARCH_TERM_SEPARATOR } from '@thehubz/shared/model';
import { Observable, Subject, Subscription, lastValueFrom, timer } from 'rxjs';
import { debounceTime, delay, tap } from 'rxjs/operators';
import { customerAssetFilters } from '../../../customer/customer-asset-filters';
import { LufthansaTableColumnData } from '../../../customer/lufthansa/lufthansa-table-column-data';
import { SeatTableColumnData } from '../../../customer/seat/seat-table-column-data';
import { VKBTableColumnData } from '../../../customer/vkb/vkb-table-column-data';
import { VorwerkTableColumnData } from '../../../customer/vorwerk/vorwerk-table-column-data';
import { AssetCommonService } from '../../../services/asset-common.service';
import { AssetFiltersService } from '../../../services/asset-filters.service';
import { AssetViewTypeService, GridConfig } from '../../../services/asset-view-type.service';
import { AssetDeletionDialogComponent } from '../../asset-detail-view/asset-deletion-dialog/asset-deletion-dialog.component';
import { AssetDownloadComponent, AssetDownloadData } from '../../asset-download/asset-download.component';
import { AssetEditPopupComponent } from '../../asset-edit-popup/asset-edit-popup.component';
import { AssetGalleryViewComponent } from '../../asset-gallery-view/asset-gallery-view.component';
import { AssetListViewComponent } from '../../asset-listview/asset-listview.component';
import { AssetUploadComponent } from '../../asset-upload/asset-upload.component';
import { TagsDialogComponent } from '../../tags-dialog/tags-dialog.component';
import { AssetsActionsComponent } from '../assets-actions/assets-actions.component';
import { isUsageRightsEdit, split } from '../assets-page.util';
import { AssetsSidebarComponent } from '../assets-sidebar/assets-sidebar.component';
import { FilterFormData } from '../filter-form-data';
import { TableColumnData } from '../table-column-data';
import { AssetsPageService } from './assets-page.service';
import { FilterBubblesUtil } from './utils/filter-bubbles.util';

@Component({
  selector: 'thehubz-assets-page',
  templateUrl: './assets-page.component.html',
  styleUrls: ['./assets-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    ClientCoreModule,
    UiKitModule,
    AssetGalleryViewComponent,
    AssetListViewComponent,
    AssetsActionsComponent,
    AssetsSidebarComponent,
  ],
  providers: [ResizeService, DatePipe, SpDateFormatPipe],
})
export class AssetsPageComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('scroller') scroller!: CdkVirtualScrollViewport;
  @ViewChild(AssetGalleryViewComponent) assetGalleryView!: AssetGalleryViewComponent;
  @ViewChild(AssetListViewComponent) assetListView!: AssetListViewComponent;
  @ViewChild(AssetsSidebarComponent) assetSidebar?: AssetsSidebarComponent;

  @Input() configObject!: AnyType;
  @Input() removedCheckedAssets!: Observable<AssetUIModel>;
  @Input() projectFilterData!: Observable<{ client: string; brand: string }>;
  @Input() showFilterBubbles = true;
  @Input() excludedAssets?: string[] = [];
  @Input() assetChecked: AssetUIModel[] = [];
  @Input() gridSettingsKeySuffix = 'AssetOverview';

  @Output() updateSelectedData = new EventEmitter<AssetUIModel[]>();
  @Output() selectedAssets = new EventEmitter<AssetUIModel[]>();

  /* Use formResetter when filter bubble is cleared */
  formResetter = new Subject<{ _id: string; name: string; key: string } | undefined>();

  itemSize = 310;
  scrollFlag = true;
  assetCount = 0;
  imageObject: AssetUIModel[] = [];
  /**
   * This 2d array is created for use with the cdkVirtualScroll
   */
  imageSplitObject!: AssetUIModel[][];
  dashboardFlag = false;
  projectFlag = false;
  loading = false;
  sortByNew = true;
  prefix = 'is-';
  size = 4;
  sizes = AppSettings.SCREEN_SIZES;
  selectedData: AssetUIModel[] = [];
  bannerImgHidden = false;
  initialFlag = true;
  imgHidden = false;
  searchText = '';
  private searchTextModelChanged = new Subject<string>();
  private searchTextTokCount = 0; // number of search terms when multiple search terms are used
  offset = 0;
  selectedFilterObject?: Record<string, AnyType>;
  queryStringObject: AnyType;
  tagsObj: { tags: string[] } = { tags: [] };
  filterObject: FilterAllOptions = {
    filters: {},
    sort: {},
    limit: 48, // divisible by 2, 3, 4 and 8
    skip: 0,
  };
  appliedFilters = 0;
  showUploadBtn = true;
  defaultSort: { [key: string]: number } = { createdAt: -1 };
  sortData: { [key: string]: number } = {};
  projectFilters!: AnyType;
  emptyStateParaText = '';
  showEmptyState = false;
  selectedViewType!: ViewType;
  selectedViewSection2: ViewType = 'sortByLastCreated';
  currentSize?: ScreenSize;
  filterBubbles: { _id: string; name: string }[] = [];
  private readonly filterBubblesUtil!: FilterBubblesUtil;
  hideBulkEdit = false;
  assetUploadStepperFlag = false;
  enableAssetDetailsLink = false;
  skipFilterProperty: string[] = []; // those will not be counted in applied filters
  readonly searchPlaceholder = SearchAssetsFieldPlaceholder;
  readonly brandingKey: CustomClients;
  tableData: AnyType;

  readonly Globally = anyGlobal;
  readonly CustomClients = CustomClients;
  readonly AccessAsset = accessAsset;
  readonly AnyAsset = anyAsset;
  readonly AssetType = Asset.AssetTypeEnum;
  filterAssetFormData!: FilterFormDataModel;
  readonly tableColumnData = TableColumnData;

  private resizeSubscription?: Subscription;
  private projectFilterSubscription?: Subscription;
  private removedCheckedAssetsSubscription?: Subscription;
  private modelSub?: Subscription;
  private exportAssetInfoSub?: Subscription;
  private usageRightUpdatedSub?: Subscription;

  dialogState?: number;
  dialogState2?: number;
  dialogEditState?: number;
  dialogRef!: MatDialogRef<AssetUploadComponent>;

  /**
   * True when data is loading from server
   */
  httpCallInProgress = false;
  isMobileSearchOpen = false;
  isSidebarOpen = false;
  isAssetSidebarEnabled = false;

  gridConfig: GridConfig = {
    showListView: false,
    showGridView: true,
    showGridViewSmall: true,
    showGridViewLarge: false,
    showGridViewSidebar: false,
  };

  /* To prevent window close if uploads are in progress in upload asset dialog */
  @HostListener('window:beforeunload', ['$event'])
  protected handleBeforeUnload() {
    if (this.dialogState === 0 && this.dialogRef.componentInstance.uploadInProgress) {
      return false;
    }
    return true;
  }

  @HostListener('window:resize', [])
  protected onResize() {
    this.detectScreenSize();
  }

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    datePipe: DatePipe,
    spDatePipe: SpDateFormatPipe,
    private router: Router,
    private canPipe: CanPipe,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private resizeSvc: ResizeService,
    private assetService: AssetsApiService,
    private commonUtilityLib: CommonUtilityLib,
    private assetsPageService: AssetsPageService,
    private assetBasketService: AssetBasketService,
    private assetCommonService: AssetCommonService,
    private notificationsService: NotificationService,
    private assetFiltersService: AssetFiltersService,
    private assetUrlService: AssetsGenerateUrlService,
    protected assetViewTypeService: AssetViewTypeService,
    private bulkEditApiService: AssetsBulkEditApiService,
    private downloadAssetsService: DownloadAssetsService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {
    this.brandingKey = this.env.brandingKey;
    this.filterBubblesUtil = new FilterBubblesUtil(datePipe, spDatePipe);

    this.isAssetSidebarEnabled = this.brandingKey === CustomClients.Vorwerk;

    this.resizeSubscription = this.resizeSvc.onResize$.pipe(delay(0)).subscribe((x) => {
      const currentSize = this.sizes.find((x) => {
        const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
        const isVisible = window.getComputedStyle(el).display != 'none';
        return isVisible;
      });
      this.currentSize = currentSize;
      if (!this.dashboardFlag || !this.projectFlag) {
        this.initAssetView();
        this.setAssetView(currentSize);
      }
      this.imageSplitObject = [];
      this.imageSplitObject = this.size ? split(this.imageObject, this.size) : [this.imageObject];
    });
    switch (this.brandingKey) {
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
        this.tableColumnData = LufthansaTableColumnData;
        break;
      case CustomClients.Vorwerk:
        this.tableColumnData = VorwerkTableColumnData;
        break;
      case CustomClients.VKB:
        this.tableColumnData = VKBTableColumnData;
        break;
      case CustomClients.SEAT:
        this.tableColumnData = SeatTableColumnData;
        break;
    }

    this.modelSub = this.searchTextModelChanged.pipe(debounceTime(600)).subscribe((term) => {
      // when user put double quotes in search text, the search should do a precise search for that asset name
      if (term && term.startsWith('"') && !term.endsWith('"')) {
        return; // search term is not complete yet, wait for closing double quotes
      }
      this.searchTextFilter();
    });
  }

  ngAfterViewInit(): void {
    this.detectScreenSize();
    timer(0).subscribe(() => {
      this.selectedViewType = this.assetViewTypeService.getViewType(this.gridSettingsKeySuffix);
    });
  }

  /**
   * For createProject and createStepper, exclude client and brand from formFieldName for filter form Object
   */
  formDataFields() {
    if (this.filterAssetFormData) {
      if (this.configObject.callFrom === 'createProject' || this.configObject.callFrom === 'createStepper') {
        this.filterAssetFormData.formFields = this.filterAssetFormData.formFields.filter(
          (formField: AnyType) => formField.formFieldName !== 'client' && formField.formFieldName !== 'brand',
        );
      }
    }
  }

  ngOnInit(): void {
    this.assetUploadStepperFlag = false;
    if (this.configObject?.callFrom === 'assetUploadStepper') {
      this.assetUploadStepperFlag = true;
    }
    this.setFilterForm();
    this.initialFlag = true;
    this._showLoader();
    if (this.configObject) this.formDataFields();
    sessionStorage.removeItem('assetsGalleryIdsList');
    this.removedCheckedAssetsSubscription = this.removedCheckedAssets?.subscribe((result) => {
      if (result._id) {
        const idx = this.assetChecked.findIndex((el) => el._id === result._id);
        if (idx != -1) {
          this.assetChecked.splice(idx, 1);
        }
      }
    });
    this.skipFilterProperty = ['createProject', 'createStepper'].includes(this.configObject?.callFrom)
      ? ['client', 'brand']
      : [AssetMetadata.General.TAGS, 'searchText'];
    switch (this.configObject && this.configObject?.callFrom) {
      case 'dashboard': {
        this.dashboardFlag = true;
        this.filterObject['limit'] = this.configObject?.noOfItems ?? 12;
        this.fetchMore(null, null, true);
        this.enableAssetDetailsLink = true;
        break;
      }
      case 'createProject':
      case 'createStepper': {
        if (this.configObject?.callFrom === 'createProject') {
          this.hideBulkEdit = true;
        }
        this._showLoader(false);
        this.projectFlag = true;
        this.showUploadBtn = false;
        this.itemSize = 212;
        this.projectFilterSubscription = this.projectFilterData.subscribe((filters: { client: string; brand: string }) => {
          this.projectFilters = filters;
          if (!filters.brand && !filters.client) {
            this.imageSplitObject = [];
            this.showEmptyState = true;
            this._showLoader(false);
            return;
          }
          this.fetchMore(filters, null, true);
        });
        break;
      }
      case 'assetUploadStepper': {
        this.showUploadBtn = false;
        this.hideBulkEdit = true;
        this.fetchMore(null, null, true);
        break;
      }
      default: {
        let filterString = undefined;
        // The queryParams is set when the library page load from the expiry date e-mails
        // Check if the current route has a query parameter 'filterString'
        if (this.route.snapshot.queryParams['filterString']) {
          // Parse the filterString from the query parameter
          filterString = JSON.parse(this.route.snapshot.queryParams['filterString']);
          // Set the initial flag to false
          this.initialFlag = false;
          // Remove the filterString query parameter from the current route
          this.router.navigate([], {
            queryParams: {
              filterString: null,
            },
            queryParamsHandling: 'merge',
          });
        }
        // Fetch more data using the filters from the parsed filterString
        this.fetchMore(filterString?.filters, null, true);
        break;
      }
    }

    // Assets overview page
    if (!this.configObject) {
      this.enableAssetDetailsLink = true;
    }
    this.exportAssetInfoSub = this.assetCommonService.exportAssetInfo.subscribe((data) => {
      if (data) this.assetsPageService.exportAssets(this.showEmptyState);
    });
    // customer specific settings
    if (this.brandingKey === CustomClients.Arval) {
      this.setVisibleCustomFields();
      this.filterBubbles = this.filterBubblesUtil.createFilterBubbles(this.filterAssetFormData, this.selectedFilterObject);
    }
    this.usageRightUpdatedSub = this.assetsPageService.usageRightUpdated$.subscribe((updated) => {
      if (updated) this.assetFindAll();
    });

    // init search terms from session storage
    const searchTerms = this.getSearchTermProperties();
    this.searchTextTokCount = Object.keys(searchTerms).length;
  }

  setFilterForm() {
    this.filterAssetFormData = customerAssetFilters(this.brandingKey) ?? FilterFormData.FORM_DATA;
    const statusField = this.filterAssetFormData.formFields?.find((field) => field.formFieldName === 'status');
    if (statusField?.options.length === 0) {
      switch (this.brandingKey) {
        case CustomClients.LufthansaMediacenter:
        case CustomClients.LufthansaMedialounge:
          statusField.options = this.assetCommonService.getLufthansaAssetFilterStatusList(this.canPipe, this.AnyAsset);
          break;
        case CustomClients.VKB:
          statusField.options = this.assetCommonService.getVKBAssetFilterStatusList(this.canPipe, this.AnyAsset, 'filterForm');
          break;
        case CustomClients.SEAT:
          statusField.options = this.assetCommonService.getSEATAssetFilterStatusList(this.canPipe, this.AnyAsset);
          break;
        case CustomClients.Standard:
          statusField.options = this.assetCommonService.getStandardAssetFilterStatusList(this.canPipe, this.AnyAsset);
          break;
        default:
          statusField.options = this.assetCommonService.getAssetFilterStatusList(this.canPipe, this.AnyAsset, 'filters');
          break;
      }
    }
  }

  private fetchMore(filterData?: AnyType, sortData?: AnyType, sortFlag?: boolean, loadMoreFlag?: boolean) {
    // Get existing filter from local storage
    const existingFilterObject: AnyType = sessionStorage.getItem(AppSettings.ASSET_FILTER_STRING);
    if (existingFilterObject && this.configObject?.callFrom !== 'createProject') {
      const getFilterData = JSON.parse(existingFilterObject);
      if (getFilterData && this.initialFlag) {
        // set filters count
        this.appliedFilters = this.commonUtilityLib.filterDataCount(getFilterData.selectedFilterData, this.skipFilterProperty);
        // selected filter object: input for filter component
        this.selectedFilterObject = getFilterData.selectedFilterData;
        if (this.selectedFilterObject?.[AssetMetadata.General.TAGS]) {
          this.tagsObj.tags = this.selectedFilterObject[AssetMetadata.General.TAGS] ?? [];
        }
        this.filterBubbles = this.filterBubblesUtil.createFilterBubbles(this.filterAssetFormData, this.selectedFilterObject);
        // reassign searched text to ngModel searchText value
        this.searchText = getFilterData.searchedText;
        this.filterObject = getFilterData;
        this.updateFilterObjectToScroll();
      } else {
        this.setFilterData(filterData, sortData, sortFlag, loadMoreFlag);
      }
    } else {
      this.setFilterData(filterData, sortData, sortFlag, loadMoreFlag);
    }
    this.initialFlag = false;
    if (this.configObject?.callFrom == 'dashboard') {
      this.filterObject['limit'] = this.configObject?.noOfItems ?? 12;
    }
    this.assetFindAll(loadMoreFlag);
  }

  /**
   * This is used to update the limit to fetch all the assets in order to scroll to particular asset
   */
  updateFilterObjectToScroll() {
    const assetFutureLimit = sessionStorage.getItem('assetFutureLimit');
    if (assetFutureLimit && assetFutureLimit != null) {
      this.filterObject.skip = parseInt(assetFutureLimit) - 48;
      this.offset = parseInt(assetFutureLimit) / 48 - 1;
      sessionStorage.removeItem('assetFutureLimit');
    }
  }

  private setFilterData(filterData?: AnyType, sortData?: AnyType, sortFlag?: boolean, loadMoreFlag?: boolean) {
    this.resetOffset(loadMoreFlag);
    this.assetFiltersService.setFilterData(
      { filterData, sortData, sortFlag, configObject: this.configObject, assetFilterString: AppSettings.ASSET_FILTER_STRING },
      this.filterObject,
      this.searchText,
    );
    const searchTextFilters = this.assetFiltersService.getSearchTextFilters(this.selectedFilterObject);
    this.selectedFilterObject = { ...this.filterObject['selectedFilterData'], ...searchTextFilters };
    this.filterBubbles = this.filterBubblesUtil.createFilterBubbles(this.filterAssetFormData, this.selectedFilterObject);
    this.appliedFilters = this.commonUtilityLib.filterDataCount(this.selectedFilterObject, this.skipFilterProperty);
  }

  private resetOffset(loadMoreFlag?: boolean) {
    if (!loadMoreFlag) {
      this.imageObject = [];
      this.offset = 0;
      this.filterObject['limit'] = 48;
      this.filterObject['skip'] = 0;
    }
  }

  /**
   * Create search filter, add it to query string and call asset find all api
   */
  private searchTextFilter() {
    if (!this.configObject || this.configObject.callFrom !== 'dashboard') {
      const tempFilterObject = { ...this.filterObject, searchedText: this.searchText.trim(), skip: 0 };
      sessionStorage.setItem(AppSettings.ASSET_FILTER_STRING, JSON.stringify(tempFilterObject));
    }
    this.resetOffset();
    this.assetFindAll();
  }

  private getSearchTermProperties() {
    const selectedFilterData = this.filterObject['selectedFilterData'];
    const searchProperties: { [key: string]: { _id: string; name: string } } = {};
    if (selectedFilterData) {
      Object.keys(selectedFilterData).forEach((key) => {
        if (key.startsWith('searchText')) {
          searchProperties[key] = selectedFilterData[key];
        }
      });
    }
    return searchProperties;
  }

  private extractSearchText() {
    let textSearch = this.searchText.trim();
    const searchProperties = this.getSearchTermProperties();
    const terms = Object.keys(searchProperties)
      .filter((key) => key.startsWith('searchText'))
      .sort()
      .map((key) => searchProperties[key].name);
    if (terms.length > 0) {
      if (textSearch) {
        terms.push(textSearch);
      }
      textSearch = terms.join(SEARCH_TERM_SEPARATOR);
    }
    return textSearch;
  }

  private extractFilters() {
    const filterObjectCopy = JSON.parse(JSON.stringify(this.filterObject));
    delete filterObjectCopy['selectedFilterData'];
    delete filterObjectCopy['searchedText'];
    return JSON.stringify(filterObjectCopy);
  }

  assetFindAll(loadMoreFlag?: boolean, config: { silent: boolean } = { silent: false }, scrollType?: string): Promise<AnyType> {
    const textSearch = this.extractSearchText();
    const filters = this.extractFilters();

    return new Promise((resolve, reject) => {
      this.assetService
        .assetsControllerFindAll(textSearch, filters)
        .pipe(tap(() => this._showLoader(false)))
        .subscribe({
          next: (response) => {
            const basketIDs = this.assetBasketService.assetIdsFromMyBasket;
            const responseData = this.assetUrlService.generatePreview(response.result ?? []);
            for (const asset of responseData) {
              asset['isAddedToBasket'] = basketIDs.includes(asset._id);
              asset['assetExpiryDays'] = this.assetCommonService.getAssetExpireDays(asset.expiryDate);
            }
            // To update the already stored assets in session storage.
            this.assetsPageService.updateAssetSessionStorage(responseData);

            timer(200).subscribe(() => {
              if (!loadMoreFlag) {
                this.imageObject = [];
              }
              this.assetCount = response.count;

              if (this.scrollFlag) {
                this.loading = false;
              } else {
                this.loading = true;
              }
              let newImageObject = [];
              if (scrollType === 'top') {
                newImageObject = [...responseData, ...this.imageObject] as AssetUIModel[];
              } else {
                newImageObject = [...this.imageObject, ...responseData] as AssetUIModel[];
              }

              const oldImageObject = [...this.imageObject];
              if (config.silent) {
                resolve(newImageObject);
                this.imageObject = oldImageObject;
                return;
              }

              if (this.configObject?.callFrom === 'assetUploadStepper') {
                this.imageObject = this.imageObject.filter((asset: AssetUIModel) => {
                  return !this.excludedAssets?.includes(asset._id);
                });
              } else {
                this.imageObject = [...newImageObject];
              }
              if (this.dashboardFlag) {
                this.size = this.configObject?.itemSize;
              }
              this.imageSplitObject = [];
              this.imageSplitObject = this.size ? split(this.imageObject, this.size) : [this.imageObject];
              this.showEmptyState = !this.imageSplitObject || this.imageSplitObject.length === 0;
              if (!this.dashboardFlag && !['createProject', 'assetUploadStepper'].includes(this.configObject?.callFrom)) {
                this.commonUtilityLib.emitNewHeaderline($localize`Asset overview: ${this.assetCount}`);
              }
              if (this.scrollFlag) {
                this.ngAfterViewInit();
              }

              resolve(this.imageObject);
            });
          },
          error: (err) => {
            this.clientNotificationHandlerService.errorNotification(err);
            reject(err);
          },
        });
    });
  }

  /**
   * To handle the editing of assets based on the provided event.
   * @param event
   */
  editAssets(event: { editType: string; assets: AssetUIModel[]; domainMatching?: boolean }) {
    this.assetsPageService.editDialogConfirmation(event.assets, event.editType).subscribe((data: AnyType) => {
      if (data) {
        // Check if the dialog edit state is invalid
        if (this.assetsPageService.isDialogEditStateInvalid(this.dialogEditState)) {
          const { editType, assets } = event;
          event.domainMatching = data?.domainMatching && data?.domainMatching === 'false' ? false : true;
          // Check if the edit type is 'usageRights' and the branding key is supported
          if (isUsageRightsEdit(editType) && this.assetsPageService.isSupportedBranding()) {
            // Handle the usage rights edit for the provided assets
            this.assetsPageService.handleUsageRightsEdit(assets);
          } else {
            // Open the edit dialog with the provided event (metadata & relations)
            this.openEditDialog(event);
          }
        }
      }
    });
  }

  showAssetMismatchDialog() {
    this.assetsPageService.showAssetMismatchDialog();
  }

  /**
   * Opens an edit dialog for the other edits (metadata & relations) of assets
   * @param event
   */
  private async openEditDialog(event: { editType: string; assets: AssetUIModel[]; domainMatching?: boolean }) {
    let getProductionDetailsEditModel: AnyType;

    if (event.editType === 'metaData' && this.brandingKey === CustomClients.Standard) {
      getProductionDetailsEditModel = await lastValueFrom(
        this.bulkEditApiService.assetsBulkEditControllerGetProductionDetailsEditModel({ ids: event.assets.map((asset) => asset._id) }),
      );
      getProductionDetailsEditModel = !getProductionDetailsEditModel.assetsWithoutProductionDetails ? getProductionDetailsEditModel : undefined;
    }

    const dialogEditRef = this.dialog.open(AssetEditPopupComponent, {
      maxWidth: '120rem',
      width: '90%',
      height: '90%',
      disableClose: true,
      data: { ...event, productionDetails: getProductionDetailsEditModel },
    });

    this.dialogEditState = dialogEditRef.getState();
    dialogEditRef.afterClosed().subscribe(async (result: { count: number; success: string; updatedAssets: AssetUIModel[] }) => {
      this.dialogEditState = undefined;
      if (result) {
        this.initialFlag = true;
        this.fetchMore();
      }
    });
  }

  download(asset: AssetUIModel) {
    const downloadData = this.downloadAssetsService.createDownloadData([asset]);
    this.dialog.open<AssetDownloadComponent, AssetDownloadData>(AssetDownloadComponent, {
      width: '100%',
      maxWidth: '70rem',
      height: '100%',
      maxHeight: '43rem',
      disableClose: true,
      data: { data: downloadData },
    });
  }

  sortToggle(event?: ViewType) {
    this.sortByNew = !this.sortByNew;
    this.selectedViewSection2 = event || 'sortByLastCreated';
    if (!this.sortByNew) {
      this.sortData = { name: 1 };
    } else {
      this.sortData = this.defaultSort;
    }
    this.filterObject['sort'] = this.sortData;
    if (this.configObject?.callFrom === 'createProject' && !this.projectFilters) return;
    this.assetFindAll();
  }

  private updateEvent(event: AnyType, tags: AnyType): AnyType | undefined {
    const hasProjectFilters = this.projectFilters && Object.keys(this.projectFilters).length > 0;
    const hasSearchText = this.searchText && this.searchText.trim().length > 0;
    const searchTerms = this.getSearchTermProperties();

    if (event || hasProjectFilters || tags || hasSearchText) {
      const updatedEvent = {
        ...(event || {}),
        ...(hasProjectFilters ? this.projectFilters : {}),
        ...(tags || {}),
        ...searchTerms,
      };
      if (hasSearchText) {
        updatedEvent['searchText'] = { _id: this.searchText, name: this.searchText };
      }
      return updatedEvent;
    } else {
      return undefined;
    }
  }

  applyFilters(event: AssetUIModel) {
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.loading = true;
    }
    const tags = this.tagsObj.tags.length ? this.tagsObj : undefined;
    event = this.updateEvent(event, tags);
    const isEmpty = Object.values(event || {}).every((x) => x === null || x === '' || x.length === 0);
    const sortData = Object.keys(this.sortData).length === 0 ? this.defaultSort : this.sortData;
    if (!isEmpty) {
      if (this.configObject?.callFrom === 'createProject' && !this.filterObject['filters']['client.name']) return;
      this.fetchMore(event, sortData, true);
    } else {
      if (this.configObject?.callFrom === 'createProject' && isEmpty) return;
      this.appliedFilters = 0;
      delete this.filterObject['filters']['$or'];
      this.fetchMore(undefined, sortData, true);
    }

    if (this.screenSizeDetectorService.isSmallScreen) {
      this.toggleSidebar();
    }
  }

  /**
   * Update form data on filter dropdown change
   * @param event
   */
  updateFormData(event: AnyType) {
    this.assetsPageService.updateFormData(event, this.filterAssetFormData, this.assetSidebar);
  }

  /**
   * Set visible property of channel and type field
   */
  private setVisibleCustomFields() {
    this.filterAssetFormData.formFields.forEach((item) => {
      switch (item.formFieldName) {
        case 'channel': {
          const productIndex = this.commonUtilityLib.findArrayIndex(this.filterAssetFormData, 'product');
          item.visible = !!this.selectedFilterObject?.[this.filterAssetFormData.formFields[productIndex].formFieldName || ''];
          break;
        }
        case 'type': {
          const channelIndex = this.commonUtilityLib.findArrayIndex(this.filterAssetFormData, 'channel');
          item.visible = !!this.selectedFilterObject?.[this.filterAssetFormData.formFields[channelIndex].formFieldName || ''];
          break;
        }
      }
    });
  }

  /**
   * Reset filter form data on menu close from the parent component
   */
  resetFormField() {
    this.assetSidebar?.filterForms.resetFormData();
    if (this.brandingKey === CustomClients.Arval) this.setVisibleCustomFields();
  }

  private detectScreenSize() {
    const currentSize = this.sizes.find((x) => {
      const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
      const isVisible = window.getComputedStyle(el).display != 'none';
      return isVisible;
    });
    this.resizeSvc.onResize(currentSize?.id);
  }

  viewAssetTags() {
    delete this.filterObject['filters']['searchText'];
    const searchText = this.extractSearchText();
    const filters: AnyType = Object.fromEntries(Object.entries(this.filterObject.filters).filter(([_, v]) => v && v != ''));
    this.dialog
      .open<TagsDialogComponent, TagsDataConfig>(TagsDialogComponent, {
        maxWidth: '40rem',
        width: '50%',
        height: '62%',
        disableClose: true,
        data: {
          selectedTags: this.selectedFilterObject?.[AssetMetadata.General.TAGS] ? this.selectedFilterObject[AssetMetadata.General.TAGS] : [],
          searchText,
          filters,
          callFrom: 'dialog',
        },
      })
      .afterClosed()
      .subscribe(async (tags) => {
        if (Array.isArray(tags)) {
          if (tags?.length) {
            this.tagsObj = { tags: tags } as AnyType;
            this.applyFilters({ ...(this.selectedFilterObject as AssetUIModel), ...this.tagsObj });
          } else {
            this.applyFilters({ ...(this.selectedFilterObject as AssetUIModel) });
          }
        }
      });
  }

  onUploadClickDraft(event?: { assets: AssetUIModel[] }): void {
    if (this.dialogState !== 0) {
      this.dialogRef = this.dialog.open(AssetUploadComponent, {
        maxWidth: '120rem',
        width: '90%',
        height: '90%',
        data: event ? { ...event, callfrom: 'draft' } : undefined,
      });
      this.dialogState = this.dialogRef.getState();
      this.dialogRef.disableClose = true;
      this.dialogRef.afterClosed().subscribe(async (result: { count: number; success: string; updatedAssets: AssetUIModel[] }) => {
        this.dialogState = this.dialogRef.getState();
        if (result && result.updatedAssets) {
          this.initialFlag = true;
          const responseData = this.assetUrlService.generatePreview(result.updatedAssets);
          this.fetchMore();
          if ((!this.env.uploadStepperConf || !this.env.uploadStepperConf?.buyoutRightSingleStepper) && this.canPipe.transform(this.AnyAsset, 'usagerights'))
            this.assetsPageService.buyoutRightsConfirmationDialog(responseData);
        }
      });
    }
  }

  onUploadClick(): void {
    if (this.dialogState !== 0) {
      this.dialogRef = this.dialog.open(AssetUploadComponent, {
        maxWidth: '120rem',
        width: '90%',
        height: '90%',
      });
      this.dialogState = this.dialogRef.getState();
      this.dialogRef.disableClose = true;
      this.dialogRef.afterClosed().subscribe((result: AssetUIModel[]) => {
        this.dialogState = this.dialogRef.getState();
        if (result && result.length) {
          const responseData = this.assetUrlService.generatePreview(result);
          for (const asset of responseData) {
            this.imageObject.unshift(asset);
          }
          this.imageSplitObject = [];
          this.imageSplitObject = split(this.imageObject, this.size);
          this.showEmptyState = !this.imageSplitObject || this.imageSplitObject.length === 0;
          if (!this.dashboardFlag && this.configObject?.callFrom !== 'createProject') {
            this.assetCount = this.assetCount + result.length;
            this.commonUtilityLib.emitNewHeaderline($localize`Asset overview: ${this.assetCount}`);
          }
          this.fetchMore();
          // if user has the usagerights role then only proceed to the dialog
          if ((!this.env.uploadStepperConf || !this.env.uploadStepperConf?.buyoutRightSingleStepper) && this.canPipe.transform(this.AnyAsset, 'usagerights'))
            this.assetsPageService.buyoutRightsConfirmationDialog(responseData);
        }
      });
    }
  }

  deleteAsset(asset: AssetUIModel, id: string, planDeletionFlag?: boolean) {
    const dialogRef = this.dialog.open(AssetDeletionDialogComponent, { data: { planDeletionFlag: planDeletionFlag } });
    dialogRef.disableClose = true;
    dialogRef
      .afterClosed()
      .pipe(
        tap((data) => {
          if (data?.confirm) {
            switch (data.actionType) {
              case 'planDeletion': {
                this.assetCommonService.plannedForDeletion(id, data.plannedDeletionObj || {}, data.deletionDate || '');
                break;
              }
              case 'deleteAsset': {
                this.assetsRemoveMultiple(asset, id);
                break;
              }
            }
          }
        }),
      )
      .subscribe();
  }

  assetsRemoveMultiple(asset: AssetUIModel, id: string) {
    this.assetService.assetsControllerRemoveMultiple(true, { assetIds: [id] }).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.clientNotificationHandlerService.successNotification($localize`Asset deleted successfully`);
          this.imageObject = this.imageObject.filter((asset: AssetUIModel) => asset._id !== id);
          this.imageSplitObject = [];
          this.imageSplitObject = split(this.imageObject, this.size);
          this.showEmptyState = !this.imageSplitObject || this.imageSplitObject.length === 0;
          // update the basket local storage if asset got deleted
          if (asset['isAddedToBasket']) {
            this.addRemoveFromBasket(asset, true);
          }
          if (!this.dashboardFlag && this.configObject?.callFrom !== 'createProject') {
            this.assetCount = this.assetCount - 1;
            this.commonUtilityLib.emitNewHeaderline($localize`Asset overview: ${this.assetCount}`);
          }
        } else {
          this.notificationsService.addNotification({
            type: 'failure',
            message: res.details.length ? res.details[0].status : $localize`Something went wrong`,
          });
        }
      },
      error: (err) => this.clientNotificationHandlerService.errorNotification(err),
    });
  }

  addRemoveFromBasket(asset: AssetUIModel, allChecked?: boolean) {
    if (this.configObject?.emitSelectedAssets) {
      if (asset.checked) {
        asset.checked = false;
        const index = this.assetChecked.findIndex((el) => el._id === asset._id);
        if (index !== -1) {
          this.assetChecked.splice(index, 1);
        }
      } else {
        asset.checked = true;
        const assetExists = this.assetChecked.find((el) => el._id === asset._id);
        if (!assetExists) {
          this.assetChecked.push(asset);
        }
      }

      return;
    }

    if (asset['isAddedToBasket']) {
      this.assetBasketService.removeFromBasket(asset._id);
    } else {
      this.assetBasketService.addToBasket(asset._id);
    }
    asset['isAddedToBasket'] = !asset['isAddedToBasket'];
    if (!allChecked) {
      this.assetsPageService.updateSelectedItems('assets', [asset], allChecked ? allChecked : false);
    }
  }

  sortAssets(event?: AnyType) {
    if (event) {
      this.filterObject['sort'] = event;
    }
    this.assetFiltersService.updateSortingSessionStorage(event, AppSettings.ASSET_FILTER_STRING);
    this.assetFindAll();
  }

  addOnEnter() {
    const searchProperties = this.getSearchTermProperties();
    if (Object.keys(searchProperties).length >= MAX_SEARCH_TERMS) {
      this.notificationsService.addNotification({
        type: 'warning',
        message: $localize`You can add maximum ${MAX_SEARCH_TERMS} search terms`,
      });
      return;
    }

    if (this.searchText) {
      // have to check if the search is already added as bubble
      if (Object.values(this.getSearchTermProperties()).some((x) => x.name.toLowerCase() === this.searchText.toLowerCase())) {
        this.searchText = '';
        return;
      }

      const filters = { ...this.selectedFilterObject };
      const propertyKey = `searchText[${this.searchTextTokCount++}]`;
      filters[propertyKey] = { _id: this.searchText, name: this.searchText };
      this.selectedFilterObject = { ...filters };
      this.searchText = ''; // reset search text, added now as bubble
      this.applyFilters(filters as AssetUIModel);
    } else {
      this.applyFilters({ ...(this.selectedFilterObject as AssetUIModel) });
    }
  }

  /**
   * Remove single filter bubble
   * @param filterBubble
   */
  removeFilterBubble(filterBubble: { _id: string; name: string; key: string }) {
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.loading = true;
    }
    this.formResetter.next(filterBubble);

    if (this.selectedFilterObject) {
      this.filterBubbles = this.filterBubblesUtil.removeFilterBubble(filterBubble, this.selectedFilterObject, this.filterObject) ?? [];
      this.fetchMore(this.selectedFilterObject);
    }

    if (this.brandingKey === CustomClients.Arval) {
      this.setVisibleCustomFields();
    }
  }

  /**
   * Clear all filter bubbles, basically a reset on filters
   */
  removeAllFilterBubble() {
    this.selectedFilterObject = {};
    this.tagsObj.tags = [];
    this.searchTextTokCount = 0;
    this.filterBubblesUtil.removeAllFilterBubble();
    this.sortData = this.defaultSort;
    if (!this.assetSidebar?.filterForms) {
      this.fetchMore();
    }
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.loading = true;
    }
    this.formResetter.next(undefined);
    if (this.brandingKey === CustomClients.Arval) {
      this.setVisibleCustomFields();
    }
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
    this.projectFilterSubscription?.unsubscribe();
    this.removedCheckedAssetsSubscription?.unsubscribe();
    this.modelSub?.unsubscribe();
    this.exportAssetInfoSub?.unsubscribe();
    this.usageRightUpdatedSub?.unsubscribe();
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }

  /**
   * SearchField methods
   */

  searchTextChanged(event: AnyType) {
    if (event) {
      this.searchText = event;
      if (this.configObject?.callFrom === 'createProject' && !this.projectFilters) {
        return;
      }
      this.searchTextModelChanged.next(this.searchText);
    } else {
      this.clearSearch();
    }
  }

  clearSearch() {
    this.searchText = '';
    this.searchTextFilter();
  }

  toggleMobileSearch() {
    this.isMobileSearchOpen = !this.isMobileSearchOpen;
    if (this.isMobileSearchOpen) {
      document.querySelector('.actions-container')?.classList.remove('mb-8');
    }
    if (!this.isMobileSearchOpen) {
      document.querySelector('.actions-container')?.classList.add('mb-8');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(_event: AnyType) {
    // ignore the search button cause it interferes by settings isMobileSearchOpen before this method is called
    if (_event.target.closest('.search-button')) return;

    if (
      this.isMobileSearchOpen &&
      !_event?.target.closest('.mobile-search') &&
      // clear button needs to be checked separately cause it's already detached from mobile search
      !_event?.target.closest('.clear-button')
    ) {
      this.hideMobileSearch();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(_event: AnyType) {
    this.hideMobileSearch();
  }

  hideMobileSearch() {
    this.isMobileSearchOpen = false;
    document.querySelector('.actions-container')?.classList.add('mb-8');
  }

  initAssetView() {
    const gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);
    if (Object.keys(gridSettings).length) {
      this.gridConfig = gridSettings;
      // close open sidebar on mobile initially
      if (this.screenSizeDetectorService.isSmallScreen && this.gridConfig.showGridViewSidebar) {
        this.toggleSidebar();
      }
      return;
    }

    // set customer additional settings
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.gridConfig.showGridViewSmall = false;
      this.gridConfig.showGridViewLarge = true;
    }
    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);

    // close open sidebar on mobile initially
    if (this.screenSizeDetectorService.isSmallScreen && this.gridConfig.showGridViewSidebar) {
      this.toggleSidebar();
    }
  }

  private setAssetView(currentSize?: ScreenSize) {
    this.size = this.assetsPageService.setAssetView(this.gridSettingsKeySuffix, currentSize);
  }

  changeAssetView(view: ViewType, toggle?: boolean) {
    if (view !== 'list') {
      this.gridConfig.showListView = false;
      this.gridConfig.showGridView = true;
      if (view === 'grid_4') {
        this.gridConfig.showGridViewSmall = true;
        this.gridConfig.showGridViewLarge = false;
        this.gridConfig.showGridViewSidebar = false;
        this.selectedViewType = 'grid_4';
        this.size = 4;
      }
      if (view === 'grid_8') {
        this.gridConfig.showGridViewSmall = false;
        this.gridConfig.showGridViewLarge = true;
        this.gridConfig.showGridViewSidebar = false;
        this.selectedViewType = 'grid_8';
        this.size = 8;
      }
      if (view === 'grid_3') {
        this.gridConfig.showGridViewSmall = true;
        this.gridConfig.showGridViewLarge = false;
        this.gridConfig.showGridViewSidebar = true;
        this.selectedViewType = 'grid_3';
        this.size = 3;
      }
      if (view === 'grid_6') {
        this.gridConfig.showGridViewSmall = false;
        this.gridConfig.showGridViewLarge = true;
        this.gridConfig.showGridViewSidebar = true;
        this.selectedViewType = 'grid_6';
        this.size = 6;
      }

      this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig); // must be called before setAssetView
      this.setAssetView(this.currentSize);
      this.imageSplitObject = [];
      this.imageSplitObject = split(this.imageObject, this.size);
    } else if (view === 'list') {
      this.gridConfig.showGridView = false;
      this.gridConfig.showGridViewSmall = false;
      this.gridConfig.showGridViewLarge = false;
      this.gridConfig.showListView = true;
      this.gridConfig.showGridViewSidebar = toggle || false;
      this.selectedViewType = 'list';
      this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
    }
  }

  selectAll(checked: boolean) {
    if (this.assetCount > 500) {
      this.filterObject.limit = 500;
    } else {
      this.filterObject.limit = this.assetCount;
    }
    this.assetFindAll(false, { silent: false }).then((assetList) => {
      if (checked) {
        const addToBasket = assetList.filter((asset: AssetUIModel) => !asset['isAddedToBasket']);
        addToBasket.forEach((asset: AssetUIModel) => {
          this.addRemoveFromBasket(asset, checked);
        });
      } else {
        const removeFromBasket = assetList.filter((asset: AssetUIModel) => asset['isAddedToBasket']);
        removeFromBasket.forEach((asset: AssetUIModel) => {
          this.addRemoveFromBasket(asset, checked);
        });
      }
      this.assetsPageService.updateSelectedItems('assets', assetList, checked);
    });
  }

  onAssetScroll($event: { offset: number; direction?: string; anchorIndex?: number }, callFrom: 'gallery' | 'list') {
    const eve = $event.offset;
    if (eve < this.offset) {
      this.offset = eve;
      this.assetFindAll(true, undefined, $event?.direction).then(() => {
        const columns = this.size || 1;

        const addedAssets = this.filterObject.limit || 0;
        const addedRows = Math.ceil(addedAssets / columns);

        setTimeout(() => {
          if ($event?.direction === 'top' && this.assetCount > 0 && $event?.anchorIndex != null) {
            if (callFrom === 'gallery') this.assetGalleryView?.scrollToIndex($event?.anchorIndex + addedRows, 'instant');
            if (callFrom === 'list') this.assetListView?.scrollToIndex($event?.anchorIndex + addedRows, 'instant');
          }
        });
      });
    } else {
      this.offset = eve;
      this.assetFindAll(true);
    }
  }

  /**
   * Whether the user has access to bulk edit
   */
  bulkEditAccessibility() {
    return (
      this.canPipe.transform(this.AnyAsset, 'delete') ||
      this.canPipe.transform(this.AnyAsset, 'download') ||
      this.canPipe.transform(this.AnyAsset, 'share') ||
      this.canPipe.transform(this.AnyAsset, 'basket.access') ||
      this.canPipe.transform(this.AnyAsset, 'collection.create')
    );
  }

  fileListSelection(event: AnyType) {
    this.assetChecked = event;
    this.selectedAssets.emit(event);
  }

  toggleSidebar() {
    if (!this.isAssetSidebarEnabled) return;

    const gridConfig: GridConfig = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);
    if (gridConfig.showGridViewSmall && !gridConfig.showGridViewSidebar) {
      this.changeAssetView('grid_3');
    } else if (gridConfig.showGridViewSmall && gridConfig.showGridViewSidebar) {
      this.changeAssetView('grid_4');
    } else if (gridConfig.showGridViewLarge && !gridConfig.showGridViewSidebar) {
      this.changeAssetView('grid_6');
    } else if (gridConfig.showGridViewLarge && gridConfig.showGridViewSidebar) {
      this.changeAssetView('grid_8');
    } else if (gridConfig.showListView && !gridConfig.showGridViewSidebar) {
      this.changeAssetView('list', true);
    } else if (gridConfig.showListView && gridConfig.showGridViewSidebar) {
      this.changeAssetView('list', false);
    }
  }
}
